import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Card, Button, TextInput, Badge, Spinner, Select, Modal, Label, Checkbox, Alert } from 'flowbite-react';
import {
  HiCheckCircle,
  HiXCircle,
  HiRefresh,
  HiSearch,
  HiPlus,
  HiPencil,
  HiShieldCheck,
  HiUser,
  HiUserAdd,
  HiUserRemove,
  HiKey,
  HiPhone,
  HiExclamationCircle,
  HiInformationCircle,
  HiTrash,
} from 'react-icons/hi';
import AdminLayout from '@/components/layout/AdminLayout';
import Pagination from '@/components/Pagination';

interface User {
  id: string;
  email: string;
  name: string;
  phone: string;
  role: 'superadmin' | 'admin' | 'user';
  status: 'pending_approval' | 'active' | 'inactive';
  created_at: string;
  updated_at: string;
}


export default function AdminUsers() {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  
  // Estados de paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [displayedUsers, setDisplayedUsers] = useState<User[]>([]);
  
  // Estados para modales
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageModal, setMessageModal] = useState({ type: 'success' as 'success' | 'error' | 'info', title: '', message: '' });
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);
  const [confirmMessage, setConfirmMessage] = useState('');
  
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [passwordUser, setPasswordUser] = useState<User | null>(null);
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'user' as 'superadmin' | 'admin' | 'user',
    status: 'active' as 'pending_approval' | 'active' | 'inactive',
  });
  const [passwordForm, setPasswordForm] = useState({
    newPassword: '',
    forceChangeOnLogin: false,
  });

  // Verificar que el usuario sea superadmin
  useEffect(() => {
    if (userProfile && userProfile.role !== 'superadmin') {
      showMessage('error', 'Acceso Denegado', 'No tienes permisos para acceder a esta página');
      navigate('/dashboard');
    }
  }, [userProfile, navigate]);

  // Cargar todos los usuarios
  const loadUsers = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading users:', error);
        showMessage('error', 'Error', 'Error al cargar usuarios');
        return;
      }

      setUsers(data || []);
      setFilteredUsers(data || []);
    } catch (error) {
      console.error('Error in loadUsers:', error);
      showMessage('error', 'Error', 'Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userProfile?.role === 'superadmin') {
      loadUsers();
    }
  }, [userProfile]);

  // Suscribirse a cambios en tiempo real en la tabla users
  useEffect(() => {
    if (userProfile?.role !== 'superadmin') return;

    console.log('Configurando suscripción Realtime para tabla users...');

    const channel = supabase
      .channel('domus-channel', {
        config: {
          broadcast: { self: true },
          presence: { key: '' },
        },
      })
      .on(
        'postgres_changes',
        {
          event: '*', // Escuchar INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'users',
        },
        (payload) => {
          console.log('🔄 Cambio detectado en tabla users:', {
            eventType: payload.eventType,
            new: payload.new,
            old: payload.old,
          });
          // Recargar la lista cuando hay cambios
          loadUsers();
        }
      )
      .subscribe((status) => {
        console.log('📡 Estado de suscripción Realtime:', status);
        if (status === 'SUBSCRIBED') {
          console.log('✅ Suscrito exitosamente a cambios en tabla users');
        } else if (status === 'CHANNEL_ERROR') {
          console.error('❌ Error en el canal Realtime');
        } else if (status === 'TIMED_OUT') {
          console.warn('⏱️ Timeout al suscribirse al canal Realtime');
        } else if (status === 'CLOSED') {
          console.log('🔒 Canal Realtime cerrado');
        }
      });

    return () => {
      console.log('🔌 Desuscribiendo del canal Realtime...');
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile?.role]);

  // Filtrar usuarios
  useEffect(() => {
    let filtered = users;

    if (searchTerm) {
      filtered = filtered.filter(
        (user) =>
          user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.phone.includes(searchTerm)
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter((user) => user.status === statusFilter);
    }

    setFilteredUsers(filtered);
    setCurrentPage(1); // Resetear a la primera página cuando cambia el filtro
  }, [searchTerm, statusFilter, users]);

  // Calcular usuarios a mostrar según paginación
  useEffect(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    setDisplayedUsers(filteredUsers.slice(startIndex, endIndex));
  }, [filteredUsers, currentPage, itemsPerPage]);

  // Funciones para mostrar modales
  const showMessage = (type: 'success' | 'error' | 'info', title: string, message: string) => {
    setMessageModal({ type, title, message });
    setShowMessageModal(true);
  };

  const showConfirm = (message: string, onConfirm: () => void) => {
    setConfirmMessage(message);
    setConfirmAction(() => onConfirm);
    setShowConfirmModal(true);
  };

  // Abrir modal de edición
  const openEditModal = (user: User) => {
    setEditingUser(user);
    setEditForm({
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || '',
      role: user.role || 'user',
      status: user.status || 'active',
    });
    setShowEditModal(true);
  };

  // Cerrar modal de edición
  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingUser(null);
    setEditForm({
      name: '',
      email: '',
      phone: '',
      role: 'user',
      status: 'active',
    });
  };

  // Abrir modal de cambio de clave
  const openPasswordModal = (user: User) => {
    setPasswordUser(user);
    setPasswordForm({
      newPassword: '',
      forceChangeOnLogin: false,
    });
    setShowPasswordModal(true);
  };

  // Cerrar modal de cambio de clave
  const closePasswordModal = () => {
    setShowPasswordModal(false);
    setPasswordUser(null);
    setPasswordForm({
      newPassword: '',
      forceChangeOnLogin: false,
    });
  };

  // Enviar email de reset de contraseña
  const sendPasswordResetEmail = async (user: User) => {
    setProcessing(user.id);
    try {
      // Usar resetPasswordForEmail que está disponible con anon key
      const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;

      showMessage('success', 'Email Enviado', `Se ha enviado un email de recuperación de contraseña a ${user.email}. El usuario recibirá un link para cambiar su contraseña.`);
    } catch (error: any) {
      console.error('Error sending password reset email:', error);
      showMessage('error', 'Error', 'Error al enviar email de recuperación: ' + (error.message || 'Error desconocido'));
    } finally {
      setProcessing(null);
    }
  };

  // Cambiar contraseña manualmente
  // NOTA: Esta función requiere Service Role Key en el backend
  // Por ahora, solo enviamos el email de recuperación

  // Guardar cambios del usuario
  const saveUserChanges = async () => {
    if (!editingUser) return;

    setProcessing(editingUser.id);
    try {
      const { error } = await supabase
        .from('users')
        .update({
          name: editForm.name,
          phone: editForm.phone,
          role: editForm.role,
          status: editForm.status,
        })
        .eq('id', editingUser.id);

      if (error) throw error;

      showMessage('success', 'Usuario Actualizado', 'El usuario ha sido actualizado exitosamente');
      closeEditModal();
      await loadUsers();
    } catch (error: any) {
      console.error('Error updating user:', error);
      showMessage('error', 'Error', 'Error al actualizar usuario: ' + (error.message || 'Error desconocido'));
    } finally {
      setProcessing(null);
    }
  };

  // Aprobar usuario
  const approveUser = async (userId: string) => {
    setProcessing(userId);
    try {
      const { error } = await supabase
        .from('users')
        .update({ status: 'active' })
        .eq('id', userId);

      if (error) throw error;

      showMessage('success', 'Usuario Aprobado', 'El usuario ha sido aprobado exitosamente');
      await loadUsers();
    } catch (error: any) {
      console.error('Error approving user:', error);
      showMessage('error', 'Error', 'Error al aprobar usuario: ' + (error.message || 'Error desconocido'));
    } finally {
      setProcessing(null);
    }
  };

  // Rechazar/Desactivar usuario
  const rejectUser = async (userId: string) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;

    // Prevenir que un superadmin se desactive
    if (user.role === 'superadmin') {
      showMessage('error', 'Acción No Permitida', 'No se puede desactivar un usuario superadmin');
      return;
    }

    showConfirm(
      `¿Estás seguro de que deseas desactivar al usuario ${user.name} (${user.email})?`,
      async () => {
        setProcessing(userId);
        try {
          const { error } = await supabase
            .from('users')
            .update({ status: 'inactive' })
            .eq('id', userId);

          if (error) throw error;

          showMessage('success', 'Usuario Desactivado', 'El usuario ha sido desactivado exitosamente');
          await loadUsers();
        } catch (error: any) {
          console.error('Error rejecting user:', error);
          showMessage('error', 'Error', 'Error al desactivar usuario: ' + (error.message || 'Error desconocido'));
        } finally {
          setProcessing(null);
        }
      }
    );
  };

  // Eliminar usuario permanentemente
  const deleteUser = async (userId: string) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;

    // Prevenir que un superadmin se elimine
    if (user.role === 'superadmin') {
      showMessage('error', 'Acción No Permitida', 'No se puede eliminar un usuario superadmin');
      return;
    }

    showConfirm(
      `¿Estás seguro de que deseas ELIMINAR permanentemente al usuario ${user.name} (${user.email})? Esta acción no se puede deshacer.`,
      async () => {
        setProcessing(userId);
        try {
          // Primero eliminar de la tabla users
          const { error: deleteError } = await supabase
            .from('users')
            .delete()
            .eq('id', userId);

          if (deleteError) throw deleteError;

          // Nota: El usuario en auth.users se mantiene por seguridad
          // Si se desea eliminar también de auth, se necesitaría usar el Admin API de Supabase

          showMessage('success', 'Usuario Eliminado', 'El usuario ha sido eliminado permanentemente');
          await loadUsers();
        } catch (error: any) {
          console.error('Error deleting user:', error);
          showMessage('error', 'Error', 'Error al eliminar usuario: ' + (error.message || 'Error desconocido'));
        } finally {
          setProcessing(null);
        }
      }
    );
  };

  const getStatusBadge = (status: string) => {
    const config = {
      active: { color: 'success' as const, label: 'Activo' },
      pending_approval: { color: 'warning' as const, label: 'Pendiente' },
      inactive: { color: 'failure' as const, label: 'Inactivo' },
    };
    const statusConfig = config[status as keyof typeof config] || config.inactive;
    return <Badge color={statusConfig.color}>{statusConfig.label}</Badge>;
  };

  const getRoleBadge = (role: string) => {
    const config = {
      superadmin: { color: 'blue' as const, label: 'Superadmin', icon: HiShieldCheck },
      admin: { color: 'gray' as const, label: 'Admin', icon: HiUserAdd },
      user: { color: 'light' as const, label: 'Usuario', icon: HiUser },
    };
    const roleConfig = config[role as keyof typeof config] || config.user;
    const Icon = roleConfig.icon;
    return (
      <Badge color={roleConfig.color} icon={Icon}>
        {roleConfig.label}
      </Badge>
    );
  };

  if (!userProfile || userProfile.role !== 'superadmin') {
    return (
      <AdminLayout>
        <Card>
          <div className="p-6">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Acceso Denegado</h3>
            <p className="text-gray-600 dark:text-gray-400">No tienes permisos para acceder a esta página</p>
          </div>
        </Card>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Mantenedor de Usuarios</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">Gestiona todos los usuarios del sistema</p>
          </div>
          <Button color="blue" className="gap-2">
            <HiPlus className="w-4 h-4" />
            Nuevo Usuario
          </Button>
        </div>

        {/* Estadísticas - Compactas */}
        <div className="grid grid-cols-3 gap-3">
          <Card className="bg-white dark:bg-gray-800 py-2 px-3">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-600 dark:text-gray-400 truncate">Total</p>
                <p className="text-lg font-bold text-gray-900 dark:text-white">{users.length}</p>
              </div>
              <HiUser className="w-5 h-5 text-blue-600 flex-shrink-0 ml-2" />
            </div>
          </Card>
          <Card className="bg-white dark:bg-gray-800 py-2 px-3">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-600 dark:text-gray-400 truncate">Activos</p>
                <p className="text-lg font-bold text-green-600">
                  {users.filter((u) => u.status === 'active').length}
                </p>
              </div>
              <HiUserAdd className="w-5 h-5 text-green-600 flex-shrink-0 ml-2" />
            </div>
          </Card>
          <Card className="bg-white dark:bg-gray-800 py-2 px-3">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-600 dark:text-gray-400 truncate">Pendientes</p>
                <p className="text-lg font-bold text-yellow-600">
                  {users.filter((u) => u.status === 'pending_approval').length}
                </p>
              </div>
              <HiUserRemove className="w-5 h-5 text-yellow-600 flex-shrink-0 ml-2" />
            </div>
          </Card>
        </div>

        {/* Filtros y búsqueda */}
        <Card className="bg-white dark:bg-gray-800">
          <div className="p-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <HiSearch className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  </div>
                  <TextInput
                    placeholder="Buscar por nombre, email o teléfono..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  <option value="all">Todos los estados</option>
                  <option value="active">Activos</option>
                  <option value="pending_approval">Pendientes</option>
                  <option value="inactive">Inactivos</option>
                </Select>
                <Select
                  value={itemsPerPage.toString()}
                  onChange={(e) => {
                    setItemsPerPage(parseInt(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="w-32"
                >
                  <option value="10">10 por página</option>
                  <option value="25">25 por página</option>
                  <option value="50">50 por página</option>
                  <option value="100">100 por página</option>
                </Select>
                <Button onClick={loadUsers} color="gray" size="sm" className="gap-2 text-gray-900 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600">
                  <HiRefresh className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </Card>

        {/* Contenido */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="xl" className="text-blue-600" />
          </div>
        ) : filteredUsers.length === 0 ? (
          <Card className="bg-white dark:bg-gray-800">
            <div className="py-12 text-center">
              <p className="text-gray-600 dark:text-gray-400 text-lg">No se encontraron usuarios</p>
            </div>
          </Card>
        ) : (
          <>
            {/* Vista de Tabla - Desktop */}
            <Card className="bg-white dark:bg-gray-800 hidden lg:block">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm text-gray-500 dark:text-gray-400">
                <thead className="bg-gray-50 text-xs uppercase text-gray-700 dark:bg-gray-700 dark:text-gray-400">
                  <tr>
                    <th scope="col" className="px-6 py-3">Nombre</th>
                    <th scope="col" className="px-6 py-3">Email</th>
                    <th scope="col" className="px-6 py-3">Teléfono</th>
                    <th scope="col" className="px-6 py-3">Rol</th>
                    <th scope="col" className="px-6 py-3">Estado</th>
                    <th scope="col" className="px-6 py-3">Fecha Registro</th>
                    <th scope="col" className="px-6 py-3">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedUsers.map((user) => (
                    <tr key={user.id} className="border-b bg-white dark:border-gray-700 dark:bg-gray-800">
                      <td className="whitespace-nowrap px-6 py-4 font-medium text-gray-900 dark:text-white">{user.name}</td>
                      <td className="px-6 py-4">{user.email}</td>
                      <td className="px-6 py-4">{user.phone}</td>
                      <td className="px-6 py-4">{getRoleBadge(user.role)}</td>
                      <td className="px-6 py-4">{getStatusBadge(user.status)}</td>
                      <td className="px-6 py-4">
                        {new Date(user.created_at).toLocaleDateString('es-ES', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          {user.status === 'pending_approval' && (
                            <>
                              <Button
                                onClick={() => approveUser(user.id)}
                                disabled={processing === user.id}
                                size="sm"
                                color="success"
                                className="gap-1"
                              >
                                {processing === user.id ? (
                                  <Spinner size="sm" />
                                ) : (
                                  <HiCheckCircle className="w-3 h-3" />
                                )}
                                Aprobar
                              </Button>
                              <Button
                                onClick={() => rejectUser(user.id)}
                                disabled={processing === user.id || user.role === 'superadmin'}
                                size="sm"
                                color="failure"
                                className="gap-1"
                              >
                                {processing === user.id ? (
                                  <Spinner size="sm" />
                                ) : (
                                  <HiXCircle className="w-3 h-3" />
                                )}
                                Rechazar
                              </Button>
                              <Button
                                onClick={() => deleteUser(user.id)}
                                disabled={processing === user.id || user.role === 'superadmin'}
                                size="sm"
                                color="failure"
                                className="gap-1"
                                title={user.role === 'superadmin' ? 'No se puede eliminar un superadmin' : 'Eliminar usuario permanentemente'}
                              >
                                {processing === user.id ? (
                                  <Spinner size="sm" />
                                ) : (
                                  <HiTrash className="w-3 h-3" />
                                )}
                              </Button>
                            </>
                          )}
                          {user.status === 'active' && (
                            <>
                              <Button
                                onClick={() => openEditModal(user)}
                                disabled={processing === user.id}
                                size="sm"
                                color="gray"
                                className="gap-1 text-gray-900 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                              >
                                <HiPencil className="w-3 h-3" />
                                Editar
                              </Button>
                              <Button
                                onClick={() => openPasswordModal(user)}
                                disabled={processing === user.id}
                                size="sm"
                                color="gray"
                                className="gap-1 text-gray-900 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                                title="Gestionar contraseña"
                              >
                                <HiKey className="w-3 h-3" />
                              </Button>
                              <Button
                                onClick={() => rejectUser(user.id)}
                                disabled={processing === user.id || user.role === 'superadmin'}
                                size="sm"
                                color="failure"
                                className="gap-1"
                                title={user.role === 'superadmin' ? 'No se puede desactivar un superadmin' : 'Desactivar usuario'}
                              >
                                {processing === user.id ? (
                                  <Spinner size="sm" />
                                ) : (
                                  <HiUserRemove className="w-3 h-3" />
                                )}
                              </Button>
                              <Button
                                onClick={() => deleteUser(user.id)}
                                disabled={processing === user.id || user.role === 'superadmin'}
                                size="sm"
                                color="failure"
                                className="gap-1"
                                title={user.role === 'superadmin' ? 'No se puede eliminar un superadmin' : 'Eliminar usuario permanentemente'}
                              >
                                {processing === user.id ? (
                                  <Spinner size="sm" />
                                ) : (
                                  <HiTrash className="w-3 h-3" />
                                )}
                              </Button>
                            </>
                          )}
                          {user.status === 'inactive' && (
                            <>
                              <Button
                                onClick={() => openEditModal(user)}
                                disabled={processing === user.id}
                                size="sm"
                                color="gray"
                                className="gap-1 text-gray-900 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                              >
                                <HiPencil className="w-3 h-3" />
                                Editar
                              </Button>
                              <Button
                                onClick={() => openPasswordModal(user)}
                                disabled={processing === user.id}
                                size="sm"
                                color="gray"
                                className="gap-1 text-gray-900 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                              >
                                <HiKey className="w-3 h-3" />
                              </Button>
                              <Button
                                onClick={() => deleteUser(user.id)}
                                disabled={processing === user.id || user.role === 'superadmin'}
                                size="sm"
                                color="failure"
                                className="gap-1"
                                title={user.role === 'superadmin' ? 'No se puede eliminar un superadmin' : 'Eliminar usuario permanentemente'}
                              >
                                {processing === user.id ? (
                                  <Spinner size="sm" />
                                ) : (
                                  <HiTrash className="w-3 h-3" />
                                )}
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Vista de Tarjetas - Móvil */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:hidden">
            {displayedUsers.map((user) => (
              <Card key={user.id} className="hover:shadow-lg transition-shadow bg-white dark:bg-gray-800">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white">{user.name}</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{user.email}</p>
                    </div>
                    <div className="flex flex-col gap-1">
                      {getRoleBadge(user.role)}
                      {getStatusBadge(user.status)}
                    </div>
                  </div>
                  <div className="space-y-2 text-sm mb-4">
                    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                      <HiPhone className="w-4 h-4" />
                      <span>{user.phone}</span>
                    </div>
                    <div className="text-gray-500 dark:text-gray-400 text-xs">
                      Registrado: {new Date(user.created_at).toLocaleDateString('es-ES')}
                    </div>
                  </div>
                  <div className="flex gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                    {user.status === 'pending_approval' && (
                      <>
                        <Button
                          onClick={() => approveUser(user.id)}
                          disabled={processing === user.id}
                          size="sm"
                          color="success"
                          className="flex-1 gap-1"
                        >
                          {processing === user.id ? (
                            <Spinner size="sm" />
                          ) : (
                            <HiCheckCircle className="w-3 h-3" />
                          )}
                          Aprobar
                        </Button>
                        <Button
                          onClick={() => rejectUser(user.id)}
                          disabled={processing === user.id || user.role === 'superadmin'}
                          size="sm"
                          color="failure"
                          className="gap-1"
                          title="Rechazar"
                        >
                          {processing === user.id ? (
                            <Spinner size="sm" />
                          ) : (
                            <HiXCircle className="w-3 h-3" />
                          )}
                        </Button>
                        {user.role !== 'superadmin' && (
                          <Button
                            onClick={() => deleteUser(user.id)}
                            disabled={processing === user.id}
                            size="sm"
                            color="failure"
                            className="gap-1"
                            title="Eliminar permanentemente"
                          >
                            {processing === user.id ? (
                              <Spinner size="sm" />
                            ) : (
                              <HiTrash className="w-3 h-3" />
                            )}
                          </Button>
                        )}
                      </>
                    )}
                    {(user.status === 'active' || user.status === 'inactive') && (
                      <>
                        <Button
                          onClick={() => openEditModal(user)}
                          disabled={processing === user.id}
                          size="sm"
                          color="gray"
                          className="flex-1 gap-1 text-gray-900 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                        >
                          <HiPencil className="w-3 h-3" />
                          Editar
                        </Button>
                        <Button
                          onClick={() => openPasswordModal(user)}
                          disabled={processing === user.id}
                          size="sm"
                          color="gray"
                          className="gap-1 text-gray-900 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                          title="Gestionar contraseña"
                        >
                          <HiKey className="w-3 h-3" />
                        </Button>
                        {user.status === 'active' && user.role !== 'superadmin' && (
                          <>
                            <Button
                              onClick={() => rejectUser(user.id)}
                              disabled={processing === user.id}
                              size="sm"
                              color="failure"
                              className="gap-1"
                              title="Desactivar usuario"
                            >
                              {processing === user.id ? (
                                <Spinner size="sm" />
                              ) : (
                                <HiUserRemove className="w-3 h-3" />
                              )}
                            </Button>
                            <Button
                              onClick={() => deleteUser(user.id)}
                              disabled={processing === user.id}
                              size="sm"
                              color="failure"
                              className="gap-1"
                              title="Eliminar usuario permanentemente"
                            >
                              {processing === user.id ? (
                                <Spinner size="sm" />
                              ) : (
                                <HiTrash className="w-3 h-3" />
                              )}
                            </Button>
                          </>
                        )}
                        {user.status === 'inactive' && user.role !== 'superadmin' && (
                          <Button
                            onClick={() => deleteUser(user.id)}
                            disabled={processing === user.id}
                            size="sm"
                            color="failure"
                            className="gap-1"
                            title="Eliminar usuario permanentemente"
                          >
                            {processing === user.id ? (
                              <Spinner size="sm" />
                            ) : (
                              <HiTrash className="w-3 h-3" />
                            )}
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Paginación */}
          {filteredUsers.length > itemsPerPage && (
            <Card className="bg-white dark:bg-gray-800 p-0">
              <Pagination
                currentPage={currentPage}
                totalPages={Math.ceil(filteredUsers.length / itemsPerPage)}
                onPageChange={(page) => setCurrentPage(page)}
                totalItems={filteredUsers.length}
                itemsPerPage={itemsPerPage}
                showInfo={true}
              />
            </Card>
          )}
        </>
        )}

        {/* Modal de Edición */}
        <Modal show={showEditModal} onClose={closeEditModal} size="md">
          <div className="p-6">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Editar Usuario
            </h3>
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-name">Nombre</Label>
                <TextInput
                  id="edit-name"
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="edit-email">Email</Label>
                <TextInput
                  id="edit-email"
                  type="email"
                  value={editForm.email}
                  disabled
                  className="mt-1 bg-gray-100 dark:bg-gray-700"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  El email no se puede modificar
                </p>
              </div>
              <div>
                <Label htmlFor="edit-phone">Teléfono</Label>
                <TextInput
                  id="edit-phone"
                  type="tel"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="edit-role">Rol</Label>
                <Select
                  id="edit-role"
                  value={editForm.role}
                  onChange={(e) => setEditForm({ ...editForm, role: e.target.value as 'superadmin' | 'admin' | 'user' })}
                  className="mt-1"
                >
                  <option value="user">Usuario</option>
                  <option value="admin">Admin</option>
                  <option value="superadmin">Superadmin</option>
                </Select>
              </div>
              <div>
                <Label htmlFor="edit-status">Estado</Label>
                <Select
                  id="edit-status"
                  value={editForm.status}
                  onChange={(e) => setEditForm({ ...editForm, status: e.target.value as 'pending_approval' | 'active' | 'inactive' })}
                  className="mt-1"
                >
                  <option value="pending_approval">Pendiente</option>
                  <option value="active">Activo</option>
                  <option value="inactive">Inactivo</option>
                </Select>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button
                onClick={saveUserChanges}
                disabled={processing === editingUser?.id}
                className="gap-2"
              >
                {processing === editingUser?.id ? (
                  <>
                    <Spinner size="sm" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <HiCheckCircle className="w-4 h-4" />
                    Guardar Cambios
                  </>
                )}
              </Button>
              <Button color="gray" onClick={closeEditModal} className="text-gray-900 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600">
                Cancelar
              </Button>
            </div>
          </div>
        </Modal>

        {/* Modal de Cambio de Contraseña */}
        <Modal show={showPasswordModal} onClose={closePasswordModal} size="md">
          <div className="p-6">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Gestionar Contraseña
            </h3>
            {passwordUser && (
              <div className="mb-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Usuario: <span className="font-semibold text-gray-900 dark:text-white">{passwordUser.name}</span>
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Email: <span className="font-semibold text-gray-900 dark:text-white">{passwordUser.email}</span>
                </p>
              </div>
            )}
            <div className="space-y-4">
              <div>
                <Label htmlFor="new-password">Nueva Contraseña</Label>
                <TextInput
                  id="new-password"
                  type="password"
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                  className="mt-1"
                  placeholder="Mínimo 6 caracteres"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Debe tener al menos 6 caracteres
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="force-change"
                  checked={passwordForm.forceChangeOnLogin}
                  onChange={(e) => setPasswordForm({ ...passwordForm, forceChangeOnLogin: e.target.checked })}
                />
                <Label htmlFor="force-change" className="flex-1 cursor-pointer">
                  Forzar cambio de contraseña en el próximo login
                </Label>
              </div>
              <Alert color="info" icon={HiInformationCircle} className="mt-4">
                <div>
                  <p className="text-sm">
                    <strong>Opción 1:</strong> Enviar email de recuperación (recomendado). El usuario recibirá un link para cambiar su contraseña.
                  </p>
                  <p className="text-sm mt-2">
                    <strong>Opción 2:</strong> Cambiar contraseña manualmente requiere un endpoint backend con Service Role Key.
                  </p>
                  {passwordForm.forceChangeOnLogin && (
                    <p className="text-sm mt-2 text-yellow-600 dark:text-yellow-400">
                      ⚠️ Si marca "Forzar cambio en próximo login", se enviará automáticamente un email de recuperación.
                    </p>
                  )}
                </div>
              </Alert>
            </div>
            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button
                onClick={() => passwordUser && sendPasswordResetEmail(passwordUser)}
                disabled={processing === passwordUser?.id}
                color="blue"
                className="gap-2"
              >
                {processing === passwordUser?.id ? (
                  <>
                    <Spinner size="sm" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <HiKey className="w-4 h-4" />
                    Enviar Email de Recuperación
                  </>
                )}
              </Button>
              <Button color="gray" onClick={closePasswordModal} className="text-gray-900 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600">
                Cancelar
              </Button>
            </div>
          </div>
        </Modal>

        {/* Modal de Confirmación */}
        <Modal show={showConfirmModal} onClose={() => setShowConfirmModal(false)} size="md">
          <div className="p-6 bg-white dark:bg-gray-800">
            <div className="flex items-center gap-3 mb-4">
              <HiExclamationCircle className="w-8 h-8 text-red-600 dark:text-red-400 flex-shrink-0" />
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                Confirmar Acción
              </h3>
            </div>
            <p className="text-gray-900 dark:text-gray-200 mb-6">{confirmMessage}</p>
            <div className="flex justify-end gap-3">
              <Button
                onClick={() => {
                  if (confirmAction) confirmAction();
                  setShowConfirmModal(false);
                  setConfirmAction(null);
                }}
                color="failure"
                className="bg-red-600 hover:bg-red-700 text-white dark:bg-red-600 dark:hover:bg-red-700 dark:text-white font-semibold px-6"
              >
                Confirmar
              </Button>
              <Button
                color="gray"
                onClick={() => {
                  setShowConfirmModal(false);
                  setConfirmAction(null);
                }}
                className="text-gray-900 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Cancelar
              </Button>
            </div>
          </div>
        </Modal>

        {/* Modal de Mensaje */}
        <Modal show={showMessageModal} onClose={() => setShowMessageModal(false)} size="md">
          <div className="p-6">
            <div className="flex items-center gap-3 mb-4">
              {messageModal.type === 'success' && <HiCheckCircle className="w-8 h-8 text-green-500" />}
              {messageModal.type === 'error' && <HiXCircle className="w-8 h-8 text-red-500" />}
              {messageModal.type === 'info' && <HiInformationCircle className="w-8 h-8 text-blue-500" />}
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                {messageModal.title}
              </h3>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-6">{messageModal.message}</p>
            <div className="flex justify-end">
              <Button color="blue" onClick={() => setShowMessageModal(false)}>
                Aceptar
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </AdminLayout>
  );
}
